import React, { useEffect, useRef } from "react";
import { useSimilarDiamondDataWithMatch } from "@apis/diamondDetail";
import { VDBText } from "@components/atoms";
import { ButtonType } from "@components/atoms/Button/types";
import FooterModel from "@components/molecules/FooterModel/FooterModel";
import { messageType } from "@components/molecules/InputPopUp/types";
import LoaderModel from "@components/molecules/LoaderModel/LoaderModel";
import MatchLabelView from "@components/molecules/MatchLabelView";
import Popup from "@components/molecules/PopUp";
import { useState } from "react";
import { Platform, TouchableOpacity } from "react-native";
import styled from "styled-components/native";
import { VDBIcons } from "@utils/VDBIcons";
import { css } from "styled-components/native";
import { useFindMatch } from "@scenes/findMatch/FindMatchContext";
import { ProductCategory } from "@utils/constants";
import { useQueryClient } from "@tanstack/react-query";
import { PriceModes } from "@components/molecules/PriceModeCard/types";

const RenderHeaderComponentView = () => {
  const {
    navigation,
    theme,
    t,
    typography,
    setItemDetail,
    itemDetail,
    otherDetail,
    userInfo,
    productType,
    currentIndex,
    setOtherItemDetail,
    setCurrentIndex,
    productColor,
  } = useFindMatch();

  const priceMode = userInfo?.user?.price_mode;
  const [showLoaderModal, setLoaderModal] = useState(false);
  const similarDetailData = useSimilarDiamondDataWithMatch();
  const [showPopUpDialog, setShowPopUpDialog] = useState(false);
  const queryClient = useQueryClient();
  const isFirstRender = useRef(true);
  const priceMarkup =
    userInfo?.user?.price_mode === PriceModes.ORANGE
      ? userInfo?.user?.markup_amount
      : 0;

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (priceMode && !isFirstRender.current && !theme.isMobile) {
      setLoaderModal(true);
      similarDetailData.mutate(
        {
          diamond_ids: itemDetail?.id,
          price_mode: priceMode,
          switch_from_stock_id: otherDetail[currentIndex]?.id,
          isLabgrown: productType === ProductCategory.LAB_GROWN_DIAMOND,
        },
        {
          async onSuccess(data) {
            const itemData = queryClient.getQueryData([
              "get_diamonds_by_id",
              itemDetail?.id,
              userInfo,
              priceMode,
              priceMarkup,
            ]);

            if (
              Array.isArray(itemData?.data?.response?.body?.diamonds) &&
              itemData.data.response.body.diamonds.length > 0
            ) {
              setItemDetail(itemData.data.response.body.diamonds[0]);
            }
            setOtherItemDetail(data?.data?.diamonds);
            setLoaderModal(false);
          },
          async onError() {
            setLoaderModal(false);
          },
        }
      );
    }
  }, [priceMode]);

  const SwitchStoneCallbackEvent = async () => {
    setLoaderModal(true);
    similarDetailData.mutate(
      {
        diamond_ids: otherDetail?.[currentIndex]?.id,
        price_mode: priceMode,
        switch_from_stock_id: itemDetail?.id,
        isLabgrown: productType === ProductCategory.LAB_GROWN_DIAMOND,
      },
      {
        async onSuccess(data) {
          setItemDetail(otherDetail?.[currentIndex]);
          setOtherItemDetail(data?.data?.diamonds);
          setCurrentIndex(0);
          setLoaderModal(false);
          setShowPopUpDialog(false);
        },
        async onError() {
          setLoaderModal(false);
        },
      }
    );
  };

  const showStoneCallbackEvent = () => {
    setShowPopUpDialog(true);
  };

  const navigateBack = () => {
    navigation.pop(2);
  };

  return (
    <>
      <PriceDetailContainer>
        <MatchLabelView
          testID="findMatch.header.matchLabel"
          leftKey={t("findMatch.yourChice.text")}
          itemLeftTextTheme={theme["core-05"]}
          keyTypographyStyle={
            typography[theme.isLargeScreen ? "Large-B" : "H3-M"]
          }
          rightKey={t("findMatch.matchStone.text")}
          centerIcon={VDBIcons.SWITCH_POSITIONS}
          ceterIconTheme={theme[productColor]}
          hideDivider={theme.isLargeScreen}
          showPadding={false}
          direction={"row"}
          keyMargin={20}
          itemContent={theme.isLargeScreen ? "space-between" : "center"}
          SwitchStoneCallback={showStoneCallbackEvent}
          OtherDetail={otherDetail}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
        />

        {!theme.isLargeScreen ? (
          <FlexMainView>
            <MainContainerView testID="findMatch.header.primary">
              <LeftLabelContainer keyMargin={15}>
                <TouchableOpacity
                  onPress={() => navigateBack()}
                  activeOpacity={0.8}
                >
                  <VDBText
                    type={typography["Body-M"]}
                    color={theme[productColor]}
                  >
                    {t("findMatch.change.text")}
                  </VDBText>
                </TouchableOpacity>
              </LeftLabelContainer>
            </MainContainerView>
            <GapView />
            <MainContainerView testID="findMatch.header.secondary">
              <LeftLabelContainer keyMargin={10}>
                <FooterModel
                  OtherDetail={otherDetail}
                  currentIndex={currentIndex}
                  setCurrentIndex={setCurrentIndex}
                  ceterIconTheme={theme["core-06"]}
                />
              </LeftLabelContainer>
            </MainContainerView>
          </FlexMainView>
        ) : null}
      </PriceDetailContainer>
      <LoaderModel
        isOpen={showLoaderModal}
        onClose={() => setLoaderModal(false)}
      />
      {showPopUpDialog && (
        <Popup
          buttons={[
            {
              type: ButtonType.primary,
              isLoadingButton: true,
              title: t("findMatch.popup.continue.text"),
              disabled: similarDetailData.isLoading,
              onPressAction: () => {
                SwitchStoneCallbackEvent();
              },
            },
            {
              type: ButtonType.secondary,
              isLoadingButton: false,
              title: t("findMatch.popup.dismiss.text"),
              onPressAction: () => {
                setShowPopUpDialog(false);
              },
            },
          ]}
          HtmlSource={{ html: t("findMatch.popup.message.text") }}
          styleKey="Body-R"
          isLoading={similarDetailData.isLoading}
          type={messageType.Error}
        />
      )}
    </>
  );
};

const PriceDetailContainer = styled.View`
  flex-direction: column;
`;

const FlexMainView = styled.View<any>`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const MainContainerView = styled.View<any>`
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  ${({ theme }) =>
    !theme.isMobile
      ? css`
          width: ${Platform.OS === "web" ? "452px" : "380px"};
        `
      : css`
          max-width: 100%;
        `}
`;

const LeftLabelContainer = styled.View<any>`
  align-items: center;
  flex-direction: row;
  justify-content: center;
  margin: ${({ keyMargin }) => `10px ${keyMargin}px`};
`;

const GapView = styled.View`
  ${({ theme }) =>
    Platform.OS === "web" &&
    !theme.isMobile &&
    css`
      width: 30px;
    `}
`;

export default RenderHeaderComponentView;
